import React, { useState } from 'react';
import { X, DollarSign } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { WithdrawalRequest } from '../../../types';
import { useAdjustUserBalance } from '../../../hooks/useApi';
import { getWithdrawalListLabel } from '../../../utils/withdrawalRequester';
import { LoadingSpinner } from '../../ui/Loading';

interface AdjustBalanceModalProps {
  withdrawal: WithdrawalRequest | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const parseErrorMessage = (error: any): string => {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  if (error?.message) {
    return error.message;
  }
  return 'An error occurred while adjusting balance';
};

const AdjustBalanceModal: React.FC<AdjustBalanceModalProps> = ({
  withdrawal,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const adjustBalanceMutation = useAdjustUserBalance();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!withdrawal) return;

    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount === 0) {
      toast.error('Please enter a valid non-zero amount');
      return;
    }

    if (!reason.trim()) {
      toast.error('Reason is required');
      return;
    }

    const playerUserId = withdrawal.userId ?? withdrawal.user?.id ?? null;
    if (!playerUserId) {
      toast.error('Cannot adjust balance: no player user linked to this withdrawal.');
      return;
    }

    try {
      await adjustBalanceMutation.mutateAsync({
        userId: playerUserId,
        data: {
          amount: parsedAmount,
          reason: reason.trim(),
        },
      });

      onSuccess?.();
      onClose();
      setAmount('');
      setReason('');
    } catch (error: any) {
      console.error('Adjust balance error:', error);
      toast.error(parseErrorMessage(error));
    }
  };

  const handleClose = () => {
    setAmount('');
    setReason('');
    onClose();
  };

  if (!isOpen || !withdrawal) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0">
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-lg font-semibold">Adjust User Balance</CardTitle>
          <Button variant="ghost" size="sm" onClick={handleClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Adjust the user's wallet balance. Use negative values to deduct funds
                (e.g., to remove fraudulent winnings).
              </p>
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="text-sm">
                  <div className="text-muted-foreground">User: {getWithdrawalListLabel(withdrawal)}</div>
                  <div className="text-muted-foreground">
                    Related withdrawal: {withdrawal.transactionId}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="amount" className="block text-sm font-medium mb-2">
                Amount ($) <span className="text-red-600">*</span>
              </label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                placeholder="e.g., -490.00 (negative to deduct)"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                Positive = add funds, Negative = deduct funds
              </p>
              {amount && !isNaN(parseFloat(amount)) && (
                <p
                  className={`text-sm font-medium mt-1 ${
                    parseFloat(amount) > 0 ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {parseFloat(amount) > 0 ? '+' : ''}${parseFloat(amount).toFixed(2)}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="reason" className="block text-sm font-medium mb-2">
                Reason <span className="text-red-600">*</span>
              </label>
              <Input
                id="reason"
                type="text"
                placeholder="e.g., Balance correction: removing fraudulent winnings"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={1000}
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                {reason.length}/1000 characters
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg">
              <p className="text-xs font-medium text-yellow-800">
                Warning: This action directly modifies the user's wallet balance.
                Please double-check the amount and reason before proceeding.
              </p>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={adjustBalanceMutation.isLoading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {adjustBalanceMutation.isLoading ? (
                  <div className="flex items-center">
                    <LoadingSpinner />
                    <span className="ml-2">Adjusting...</span>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <DollarSign className="h-4 w-4 mr-2" />
                    Adjust Balance
                  </div>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdjustBalanceModal;
